import { useMemo, useState } from "react";
import axios from "axios";
import { Sparkles } from "lucide-react";
import { API } from "../../App";
import { calculateNaviPartnerPricing, formatMoney } from "../../lib/meetupPricing";
import { CARIBBEAN_ISLANDS } from "../../lib/caribbeanIslands";
import NaviPricingBreakdown from "./NaviPricingBreakdown";
import PartnerMapPicker from "./PartnerMapPicker";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";

const COUNTRIES = CARIBBEAN_ISLANDS.map((i) => ({ code: i.code, label: i.name }));

function generateSpecialDraft({ businessName, country, category }) {
  const place = COUNTRIES.find((item) => item.code === country)?.label || "the Caribbean";
  return `${businessName} delivers an authentic ${place} ${category} moment with premium hospitality, trusted local guidance, and a clear path from discovery to booking.`;
}

export default function QuickOnboard() {
  const [form, setForm] = useState({
    businessName: "",
    country: "barbados",
    category: "experience",
    lat: null,
    lng: null,
    parish: "",
    specialNote: "",
    contactEmail: "",
    projectedPayout: 100,
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const pinLabel = useMemo(() => {
    if (form.lat != null && form.lng != null) {
      return `WGS84: ${form.lat.toFixed(5)}, ${form.lng.toFixed(5)}`;
    }
    return "Tap the map to set your business coordinates";
  }, [form.lat, form.lng]);
  const projectedPricing = useMemo(
    () =>
      calculateNaviPartnerPricing({
        desiredPayout: form.projectedPayout,
        country: form.country,
        partnerTier: "standard",
      }),
    [form.projectedPayout, form.country]
  );

  const updateField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const handleMapPick = ({ lat, lng }) => {
    setForm((current) => ({ ...current, lat, lng }));
  };

  const handleGenerateCopy = () => {
    updateField(
      "specialNote",
      generateSpecialDraft({
        businessName: form.businessName || "This business",
        country: form.country,
        category: form.category,
      })
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setResult(null);
    try {
      if (form.lat == null || form.lng == null) {
        setResult({ status: "error", partner_alert: "Set your business on the map first." });
        setSubmitting(false);
        return;
      }
      const response = await axios.post(`${API}/partners/quick-onboard`, {
        business_name: form.businessName,
        country: form.country,
        category: form.category,
        lat: form.lat,
        lng: form.lng,
        parish: form.parish || null,
        special_note: form.specialNote,
        contact_email: form.contactEmail || null,
        projected_payout: form.projectedPayout,
        projected_customer_total: projectedPricing.totalPrice,
      });
      setResult(response.data);
      window.dispatchEvent(new Event("navi-live-status-refresh"));
    } catch (error) {
      setResult({
        status: "error",
        partner_alert:
          error.response?.data?.detail || "Unable to save this partner right now.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-[2rem] border border-slate-200 bg-white shadow-xl p-6">
      <div className="mb-5">
        <p className="text-sm uppercase tracking-[0.18em] text-orange-500 font-semibold">
          Partner Portal
        </p>
        <h3 className="text-2xl font-serif font-bold text-slate-900">
          Quick Onboard
        </h3>
        <p className="text-sm text-slate-500 mt-2">
          One clean funnel: business name, map pin, and a polished AI-assisted pitch.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="text-sm font-medium text-slate-700 mb-2 block">1. Name of Business</label>
          <Input
            value={form.businessName}
            onChange={(event) => updateField("businessName", event.target.value)}
            placeholder="Ocean Crest Surf School"
            className="rounded-2xl"
            required
          />
        </div>

        <div>
          <label className="text-sm font-medium text-slate-700 mb-2 block">Island</label>
          <div className="flex flex-wrap gap-2">
            {COUNTRIES.map((country) => (
              <button
                key={country.code}
                type="button"
                onClick={() =>
                  setForm((current) => ({
                    ...current,
                    country: country.code,
                    lat: null,
                    lng: null,
                  }))
                }
                className={`px-4 py-2 rounded-full text-sm font-medium ${
                  form.country === country.code
                    ? "bg-slate-900 text-white"
                    : "bg-slate-100 text-slate-700"
                }`}
              >
                {country.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-slate-700 mb-2 block">2. Pin on Map (real coordinates)</label>
          <PartnerMapPicker
            country={form.country}
            lat={form.lat}
            lng={form.lng}
            onChange={handleMapPick}
          />
          <p className="text-xs text-slate-500 mt-2">{pinLabel}</p>
          <Input
            value={form.parish}
            onChange={(event) => updateField("parish", event.target.value)}
            placeholder="Parish / district (e.g. Christ Church)"
            className="rounded-2xl mt-2"
          />
        </div>

        <div>
          <div className="flex items-center justify-between gap-3 mb-2">
            <label className="text-sm font-medium text-slate-700">Projected Earnings</label>
            <Badge className="bg-slate-900 text-white border-0">
              {formatMoney(form.projectedPayout)}
            </Badge>
          </div>
          <input
            type="range"
            min="50"
            max="400"
            step="5"
            value={form.projectedPayout}
            onChange={(event) => updateField("projectedPayout", Number(event.target.value))}
            className="w-full accent-slate-900"
          />
          <div className="grid sm:grid-cols-3 gap-3 mt-4">
            <div className="rounded-2xl bg-slate-950 text-white p-4">
              <p className="text-xs uppercase tracking-wide text-slate-300 mb-1">Customer Pays</p>
              <p className="text-lg font-bold">{formatMoney(projectedPricing.totalPrice)}</p>
            </div>
            <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4">
              <p className="text-xs uppercase tracking-wide text-emerald-700 mb-1">You Keep</p>
              <p className="text-lg font-bold text-slate-900">{formatMoney(projectedPricing.businessPayout)}</p>
            </div>
            <div className="rounded-2xl bg-amber-50 border border-amber-100 p-4">
              <p className="text-xs uppercase tracking-wide text-amber-700 mb-1">We Handle</p>
              <p className="text-sm font-medium text-slate-900">
                Tax filings, card processing, and Mansion platform services.
              </p>
            </div>
          </div>
          <NaviPricingBreakdown pricing={projectedPricing} />
        </div>

        <div>
          <div className="flex items-center justify-between gap-3 mb-2">
            <label className="text-sm font-medium text-slate-700">3. What makes you special?</label>
            <Button type="button" variant="outline" className="rounded-full" onClick={handleGenerateCopy}>
              <Sparkles className="w-4 h-4 mr-2" />
              AI Assist
            </Button>
          </div>
          <textarea
            value={form.specialNote}
            onChange={(event) => updateField("specialNote", event.target.value)}
            placeholder="Tell Navi what guests remember most about you..."
            className="w-full min-h-[120px] rounded-[1.5rem] border border-slate-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-cyan-500"
            required
          />
        </div>

        <div>
          <label className="text-sm font-medium text-slate-700 mb-2 block">Contact Email</label>
          <Input
            type="email"
            value={form.contactEmail}
            onChange={(event) => updateField("contactEmail", event.target.value)}
            placeholder="owner@business.com"
            className="rounded-2xl"
          />
        </div>

        <Button type="submit" className="w-full rounded-full py-6" disabled={submitting}>
          {submitting ? "Sending to Mansion Engine..." : "List Your Experience"}
        </Button>
      </form>

      {result && (
        <div className="mt-5 rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-medium text-emerald-800">{result.partner_alert}</p>
            <Badge className="bg-emerald-100 text-emerald-700 border-0">
              {result.saved_to_supabase ? "Connected to Mansion Global Network" : "Held safely for Mansion sync"}
            </Badge>
          </div>
        </div>
      )}
    </div>
  );
}
